import { DepositInput } from '../../DepositInput';
import { formatMinorAmount } from '../../../utils/formatScheduleCurrency';
import type { Product } from '@/features/products/types/products.types';
import { fixedDepositStatement, SCHEDULE_SUGGESTED_DEPOSIT_HINT } from './copy';
import { DepositCeilingNotice } from './PaymentNotices';
import type { DepositMode, DepositOrigin } from './depositMode';

interface DepositSectionProps {
    selectedProduct: Product;
    effectiveMaxExclusive: number | undefined;
    configuredMaxBinds: boolean;
    configuredMaxDepositMinor: number | undefined;
    naturalCeilingLabel: string;
    mode: DepositMode;
    currency: string;
    depositMinor: number | undefined;
    depositDiscards: number;
    depositOrigin: DepositOrigin;
    onDepositInput: (value: number | undefined) => void;
    onDepositEdit: () => void;
    scheduleSetDepositMinor: number | undefined;
    isDepositWriteBackPending: boolean;
    ceilingRejectionMinor: number | undefined;
}

export function DepositSection({
    selectedProduct,
    effectiveMaxExclusive,
    configuredMaxBinds,
    configuredMaxDepositMinor,
    naturalCeilingLabel,
    mode,
    currency,
    depositMinor,
    depositDiscards,
    depositOrigin,
    onDepositInput,
    onDepositEdit,
    scheduleSetDepositMinor,
    isDepositWriteBackPending,
    ceilingRejectionMinor,
}: DepositSectionProps) {
    const fixedStatement = mode.kind === 'fixed' ? fixedDepositStatement(mode.amountMinor, currency) : null;

    return (
        <div className="space-y-1.5">
            {fixedStatement !== null ? (
                <p data-testid="fixed-deposit-statement" role="status">
                    {fixedStatement.lead}
                    {fixedStatement.figure !== undefined && (
                        <>
                            {': '}
                            <span className="font-medium text-mp-foreground">
                                {fixedStatement.figure}
                            </span>
                        </>
                    )}
                </p>
            ) : (
                // Keystrokes claim the value before the input's debounced report, so a discard cannot remount a draft.
                <div onChange={onDepositEdit}>
                    <DepositInput
                        key={depositDiscards}
                        minDepositMinor={selectedProduct.minDepositAmountMinor ?? 0}
                        maxDepositMinor={effectiveMaxExclusive ?? 0}
                        maxDepositLabel={
                            configuredMaxBinds
                                ? 'the maximum deposit for this product'
                                : naturalCeilingLabel
                        }
                        inclusiveMaxDisplayMinor={
                            configuredMaxBinds ? configuredMaxDepositMinor : undefined
                        }
                        currency={currency}
                        valueMinor={depositMinor}
                        onDebouncedChange={onDepositInput}
                    />
                </div>
            )}
            <DepositCeilingNotice
                rejectionMinor={ceilingRejectionMinor}
                configuredMaxBinds={configuredMaxBinds}
                configuredMaxDepositMinor={configuredMaxDepositMinor}
                currency={currency}
                fixedAmountMinor={mode.kind === 'fixed' ? mode.amountMinor : undefined}
                naturalCeilingLabel={naturalCeilingLabel}
            />
            {/*
             * Region stays mounted so it exists in the a11y tree before content
             * appears; keep the wrapper unstyled so it self-collapses when empty.
             */}
            <div data-testid="applied-deposit-hint-region" role="status">
                {depositOrigin === 'schedule' && (
                    <p
                        data-testid="suggested-deposit-hint"
                        className="text-xs text-mp-muted"
                    >
                        {SCHEDULE_SUGGESTED_DEPOSIT_HINT}
                    </p>
                )}
                {depositOrigin !== 'schedule' &&
                    !isDepositWriteBackPending &&
                    scheduleSetDepositMinor !== undefined && (
                    <p
                        data-testid="applied-deposit-hint"
                        className="text-xs text-mp-muted"
                    >
                        Deposit set by the payment schedule:{' '}
                        <span className="font-medium text-mp-foreground">
                            {formatMinorAmount(scheduleSetDepositMinor, currency)}
                        </span>
                        <span className="block">
                            That is the first payment in the schedule below. Enter an amount above to set a
                            different deposit.
                        </span>
                    </p>
                )}
            </div>
        </div>
    );
}
